// Newsletter & Kampagnen: SMTP (Outlook 365 / netcup) · Kontakte · A/B-Kampagnen · Statistik
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Mail, Server, Users, Plus, Send, Loader2, Trophy, FlaskConical, Trash2, Wand2, Check, Sparkles, Eye, Pencil, Image as ImageIcon, Upload, X, BarChart3 } from "lucide-react";
import { CampaignReport } from "./CampaignReport";
import { BrevoPanel } from "./BrevoPanel";
import { CanvaPanel } from "./CanvaPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", subject_a: "", body_a: "", subject_b: "", body_b: "", test_share_pct: 50, segment_tag: "", cta_url: "https://www.schwalbennest-ruegen.de", cta_label: "Jetzt ansehen", preheader: "", hero_media_id: null, bg_media_id: null, footer: "" };
const DEFAULT_TEST_TO = "fh@schwalbennest-ruegen.com, hm@schwalbennest-ruegen.de, ab@schwalbennest-ruegen.de, js@schwalbennest-ruegen.de";

export function Newsletter() {
  const [msg, setMsg] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [contacts, setContacts] = useState({ total: 0, tags: {}, contacts: [] });
  const [editing, setEditing] = useState(null);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 5000); };
  const load = async () => {
    const [c, k] = await Promise.all([axios.get(`${API}/newsletter/campaigns`), axios.get(`${API}/newsletter/contacts`)]);
    setCampaigns(c.data); setContacts(k.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="newsletter-view">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Mail size={22} className="text-eb-primary" /> Newsletter &amp; Kampagnen</h2>
          <p className="text-eb-muted text-sm">Eigenes Mailing-Tool · A/B-Splitting mit Signifikanz-Test · Öffnungs-/Klick-Tracking · Versand nach Freigabe</p>
        </div>
        {msg && <span className="text-emerald-400 text-xs" data-testid="newsletter-msg">{msg}</span>}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5">
        <div className="space-y-4">
          <CampaignEditor tags={contacts.tags} editing={editing} onCancel={() => setEditing(null)} flash={flash}
            onCreated={() => { setEditing(null); load(); flash("✓ Kampagne gespeichert"); }} />
          <CampaignList campaigns={campaigns} reload={load} flash={flash} onEdit={(c) => { setEditing(c); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        </div>
        <div className="space-y-4">
          <SmtpSettings flash={flash} />
          <BrevoPanel flash={flash} />
          <ContactsPanel contacts={contacts} reload={load} flash={flash} />
          <CanvaPanel flash={flash} compact />
        </div>
      </div>
    </div>
  );
}

function SmtpSettings({ flash }) {
  const [presets, setPresets] = useState({});
  const [cfg, setCfg] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/newsletter/smtp/presets`).then((r) => setPresets(r.data));
    axios.get(`${API}/newsletter/smtp`).then((r) => setCfg({ ...r.data, password: "" }));
  }, []);
  if (!cfg) return null;
  const pick = (k) => setCfg({ ...cfg, preset: k, host: presets[k].host, port: presets[k].port, security: presets[k].security });
  const save = async () => { await axios.put(`${API}/newsletter/smtp`, cfg); flash("✓ SMTP gespeichert"); };
  const test = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/newsletter/smtp/test`, { to: testTo }); flash(r.data.message); }
    catch (e) { flash(e.response?.data?.detail || "Test fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const f = (k, ph, type = "text") => (
    <input type={type} placeholder={ph} value={cfg[k] ?? ""} data-testid={`smtp-${k}`}
      onChange={(e) => setCfg({ ...cfg, [k]: type === "number" ? parseInt(e.target.value || 0) : e.target.value })} />
  );
  return (
    <div className="card space-y-2" data-testid="smtp-settings">
      <div className="overline flex items-center gap-1"><Server size={12} /> Versand-Server</div>
      <div className="flex gap-1 eb-surface rounded-full p-1 w-fit">
        {[["smtp", "SMTP"], ["brevo", "Brevo"]].map(([m, l]) => (
          <button key={m} onClick={() => setCfg({ ...cfg, transport: m })} data-testid={`smtp-transport-${m}`}
            className={`px-3 py-0.5 rounded-full text-xs font-semibold ${(cfg.transport || "smtp") === m ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted"}`}>
            {l}
          </button>
        ))}
      </div>
      {(cfg.transport || "smtp") === "brevo" ? (
        <div className="text-[11px] text-eb-muted" data-testid="smtp-brevo-hint">Versand läuft über die Brevo-API (Konfiguration im Brevo-Panel unten). A/B-Tracking bleibt aktiv.</div>
      ) : (<>
      <select value={cfg.preset} onChange={(e) => pick(e.target.value)} data-testid="smtp-preset">
        {Object.entries(presets).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <div className="grid grid-cols-[1fr_70px] gap-1">{f("host", "Host")}{f("port", "Port", "number")}</div>
      <select value={cfg.security} onChange={(e) => setCfg({ ...cfg, security: e.target.value })} data-testid="smtp-security">
        <option value="starttls">STARTTLS</option><option value="ssl">SSL/TLS</option>
      </select>
      {f("username", "Benutzer (z. B. info@ihre-domain.de)")}
      <input type="password" placeholder={cfg.has_password ? "•••• gespeichert" : "Passwort / App-Passwort"} value={cfg.password}
        onChange={(e) => setCfg({ ...cfg, password: e.target.value })} data-testid="smtp-password" />
      <div className="grid grid-cols-2 gap-1">{f("from_name", "Absender-Name")}{f("from_email", "Absender-E-Mail")}</div>
      </>)}
      <div className="flex gap-1 eb-surface rounded-full p-1 w-fit">
        {["simulation", "live"].map((m) => (
          <button key={m} onClick={() => setCfg({ ...cfg, mode: m })} data-testid={`smtp-mode-${m}`}
            className={`px-3 py-0.5 rounded-full text-xs font-semibold ${cfg.mode === m ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted"}`}>
            {m === "simulation" ? "Simulation" : "LIVE"}
          </button>
        ))}
      </div>
      <button className="btn btn-primary w-full justify-center" onClick={save} data-testid="smtp-save"><Check size={14} /> Speichern</button>
      <div className="flex gap-1">
        <input placeholder="Testmail an …" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="smtp-test-to" />
        <button className="btn" onClick={test} disabled={busy || !testTo} data-testid="smtp-test-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : "Test"}</button>
      </div>
      <div className="text-[10px] text-eb-muted">Outlook 365: smtp.office365.com:587 STARTTLS (SMTP-AUTH im M365-Admin aktivieren). netcup: Ihren Mailserver (mxXXXX.netcup.net) aus dem CCP eintragen, Port 465 SSL.</div>
    </div>
  );
}

function ContactsPanel({ contacts, reload, flash }) {
  const [c, setC] = useState({ email: "", name: "", tags: "" });
  const add = async () => {
    try {
      await axios.post(`${API}/newsletter/contacts`, { email: c.email, name: c.name, tags: c.tags.split(",").map((t) => t.trim()).filter(Boolean).concat("newsletter") });
      setC({ email: "", name: "", tags: "" }); reload(); flash("✓ Kontakt hinzugefügt");
    } catch (e) { flash(e.response?.data?.detail || "Fehler"); }
  };
  return (
    <div className="card space-y-2" data-testid="contacts-panel">
      <div className="overline flex items-center gap-1"><Users size={12} /> Empfänger ({contacts.total})</div>
      <div className="flex flex-wrap gap-1">
        {Object.entries(contacts.tags).map(([t, n]) => <span key={t} className="channel-pill bg-white/5 text-eb-muted">{t} · {n}</span>)}
      </div>
      <input placeholder="E-Mail" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} data-testid="contact-email" />
      <input placeholder="Name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="contact-name" />
      <input placeholder="Tags (kommagetrennt)" value={c.tags} onChange={(e) => setC({ ...c, tags: e.target.value })} data-testid="contact-tags" />
      <button className="btn w-full justify-center" onClick={add} disabled={!c.email} data-testid="contact-add"><Plus size={13} /> Kontakt hinzufügen</button>
      <ContactImport reload={reload} flash={flash} />
    </div>
  );
}

function ContactImport({ reload, flash }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef();
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/newsletter/contacts/import`, { text, tags: ["newsletter", "gast"] });
      flash(`✓ Import: ${r.data.added} neu · ${r.data.updated} aktualisiert · ${r.data.invalid} ungültig`); setText(""); reload();
    } catch (e) { flash(e.response?.data?.detail || "Import fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const onFile = (e) => { const f = e.target.files?.[0]; if (!f) return; const rd = new FileReader(); rd.onload = () => setText(rd.result); rd.readAsText(f); };
  return (
    <div className="pt-2 border-t border-white/5 space-y-1" data-testid="contact-import">
      <div className="overline flex items-center gap-1"><Upload size={11} /> Gästeliste importieren (CSV / Einfügen)</div>
      <textarea rows={3} placeholder={"E-Mail; Name\nmax@beispiel.de; Max Mustermann\nanna@beispiel.de, Anna"} value={text} onChange={(e) => setText(e.target.value)} data-testid="contact-import-text" />
      <div className="flex gap-1">
        <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={onFile} data-testid="contact-import-file" />
        <button className="btn flex-1 justify-center" onClick={() => fileRef.current.click()} data-testid="contact-import-pick">CSV wählen</button>
        <button className="btn btn-primary flex-1 justify-center" onClick={run} disabled={busy || !text.trim()} data-testid="contact-import-run">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Importieren
        </button>
      </div>
    </div>
  );
}

function CampaignEditor({ tags, onCreated, editing, onCancel, flash }) {
  const [c, setC] = useState(EMPTY);
  const [busy, setBusy] = useState("");
  const [gen, setGen] = useState({ topic: "Herbstfest im Schwalbennest", details: "" });
  const [variants, setVariants] = useState([]);
  const [media, setMedia] = useState([]);
  useEffect(() => { if (editing) setC({ ...EMPTY, ...editing, subject_b: editing.subject_b || "", body_b: editing.body_b || "", segment_tag: editing.segment_tag || "" }); else setC(EMPTY); }, [editing]);
  useEffect(() => { axios.get(`${API}/marketing/media`).then((r) => setMedia(r.data.filter((m) => m.kind === "image"))).catch(() => {}); }, [editing]);
  const set = (k) => (e) => setC({ ...c, [k]: k === "test_share_pct" ? parseInt(e.target.value) : e.target.value });
  const improve = async (k) => {
    setBusy(k);
    try {
      const r = await axios.post(`${API}/marketing/ai/improve`, { text: c[k] || c.name, platform: "newsletter",
        instruction: k.startsWith("subject") ? "Schreibe eine kurze, öffnungsstarke Betreffzeile (max 60 Zeichen)" : "Schreibe einen ansprechenden Newsletter-Text mit klarer Call-to-Action" });
      setC((x) => ({ ...x, [k]: r.data.text }));
    } finally { setBusy(""); }
  };
  const generate = async () => {
    setBusy("gen");
    try {
      const r = await axios.post(`${API}/newsletter/campaigns/generate`, { topic: gen.topic, details: gen.details, variants: 3 });
      setVariants(r.data.variants);
      setC((x) => ({ ...x, name: x.name || r.data.name, preheader: r.data.preheader || x.preheader, cta_label: r.data.cta_label || x.cta_label,
        subject_a: r.data.variants[0].subject, body_a: r.data.variants[0].body,
        subject_b: r.data.variants[1]?.subject || "", body_b: r.data.variants[1]?.body || "" }));
      flash(`✓ ${r.data.variants.length} Varianten erzeugt – A und B vorbelegt`);
    } catch (e) { flash(e.response?.data?.detail || "KI-Erzeugung fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const applyVariant = (v, slot) => setC((x) => ({ ...x, [`subject_${slot}`]: v.subject, [`body_${slot}`]: v.body }));
  const save = async () => {
    setBusy("save");
    const body = { ...c, segment_tag: c.segment_tag || null, subject_b: c.subject_b || null, body_b: c.body_b || null, hero_media_id: c.hero_media_id || null, bg_media_id: c.bg_media_id || null };
    delete body.stats; delete body.id; delete body.status; delete body.created_at; delete body.ab; delete body.winner_sent; delete body.updated_at; delete body.sent_at; delete body.last_test_at; delete body.last_test_to; delete body.brevo_campaign_id; delete body.brevo_pushed_at;
    try {
      if (editing) await axios.put(`${API}/newsletter/campaigns/${editing.id}`, body); else await axios.post(`${API}/newsletter/campaigns`, body);
      setC(EMPTY); setVariants([]); onCreated();
    } catch (e) { flash(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const aiBtn = (k) => (
    <button className="text-[11px] text-eb-primary flex items-center gap-0.5" onClick={() => improve(k)} data-testid={`nl-ai-${k}`}>
      {busy === k ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} KI
    </button>
  );
  return (
    <div className="card space-y-3" data-testid="campaign-editor">
      <div className="flex items-center justify-between">
        <div className="overline">{editing ? `Kampagne bearbeiten · ${editing.name}` : "Neue Kampagne"}</div>
        {editing && <button className="btn !py-1 text-xs" onClick={onCancel} data-testid="nl-cancel-edit"><X size={12} /> Abbrechen</button>}
      </div>
      <div className="eb-surface rounded-md p-3 space-y-2" data-testid="nl-generator">
        <div className="text-xs font-bold flex items-center gap-1"><Sparkles size={12} className="text-eb-primary" /> KI-Varianten erzeugen</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
          <input placeholder="Thema" value={gen.topic} onChange={(e) => setGen({ ...gen, topic: e.target.value })} data-testid="nl-gen-topic" />
          <input placeholder="Details: Datum, Uhrzeit, Programm, Angebot …" value={gen.details} onChange={(e) => setGen({ ...gen, details: e.target.value })} data-testid="nl-gen-details" />
          <button className="btn btn-primary" onClick={generate} disabled={busy === "gen" || !gen.topic} data-testid="nl-gen-run">
            {busy === "gen" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} 3 Varianten
          </button>
        </div>
        {variants.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="nl-variants">
            {variants.map((v, i) => (
              <div key={i} className="bg-black/20 rounded-md p-2 text-[11px] space-y-1" data-testid={`nl-variant-${i}`}>
                <div className="font-semibold">{v.subject}</div>
                <div className="text-eb-muted line-clamp-4">{v.body}</div>
                <div className="flex gap-1">
                  <button className="btn !py-0.5 !px-1.5 text-[10px]" onClick={() => applyVariant(v, "a")} data-testid={`nl-variant-${i}-a`}>→ A</button>
                  <button className="btn !py-0.5 !px-1.5 text-[10px]" onClick={() => applyVariant(v, "b")} data-testid={`nl-variant-${i}-b`}>→ B</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <input placeholder="Kampagnen-Name" value={c.name} onChange={set("name")} data-testid="nl-name" />
      <input placeholder="Vorschautext (Preheader, erscheint im Posteingang neben dem Betreff)" value={c.preheader} onChange={set("preheader")} data-testid="nl-preheader" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {["a", "b"].map((v) => (
          <div key={v} className="space-y-1 p-3 rounded-md eb-surface">
            <div className="flex justify-between"><span className="text-xs font-bold">Variante {v.toUpperCase()}{v === "b" && " (optional)"}</span>{aiBtn(`subject_${v}`)}</div>
            <input placeholder="Betreff" value={c[`subject_${v}`]} onChange={set(`subject_${v}`)} data-testid={`nl-subject-${v}`} />
            <div className="flex justify-end">{aiBtn(`body_${v}`)}</div>
            <textarea rows={7} placeholder="Inhalt (Absätze durch Leerzeile)" value={c[`body_${v}`]} onChange={set(`body_${v}`)} data-testid={`nl-body-${v}`} />
          </div>
        ))}
      </div>
      <div>
        <div className="overline mb-1 flex items-center gap-1"><ImageIcon size={11} /> Kopfbild (aus Mediathek / Canva)</div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="nl-hero-picker">
          <button className={`shrink-0 w-20 h-14 rounded-md text-[10px] eb-surface ${!c.hero_media_id ? "ring-2 ring-eb-primary" : ""}`} onClick={() => setC({ ...c, hero_media_id: null })} data-testid="nl-hero-none">ohne Bild</button>
          {media.map((m) => (
            <button key={m.id} className={`shrink-0 w-20 h-14 rounded-md overflow-hidden ${c.hero_media_id === m.id ? "ring-2 ring-eb-primary" : ""}`} onClick={() => setC({ ...c, hero_media_id: m.id })} data-testid={`nl-hero-${m.id}`} title={m.title || m.id}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="overline mb-1 flex items-center gap-1"><ImageIcon size={11} /> Hintergrund-Struktur (z. B. Knitterpapier)</div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="nl-bg-picker">
          <button className={`shrink-0 w-14 h-14 rounded-md text-[10px] eb-surface ${!c.bg_media_id ? "ring-2 ring-eb-primary" : ""}`} onClick={() => setC({ ...c, bg_media_id: null })} data-testid="nl-bg-none">glatt</button>
          {media.map((m) => (
            <button key={m.id} className={`shrink-0 w-14 h-14 rounded-md overflow-hidden ${c.bg_media_id === m.id ? "ring-2 ring-eb-primary" : ""}`} onClick={() => setC({ ...c, bg_media_id: m.id })} data-testid={`nl-bg-${m.id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <div className="overline mb-1">Segment</div>
          <select value={c.segment_tag} onChange={set("segment_tag")} data-testid="nl-segment">
            <option value="">Alle Abonnenten</option>
            {Object.keys(tags).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div className="overline mb-1">Testgruppe je Variante {c.test_share_pct} %</div>
          <input type="range" min="5" max="50" value={c.test_share_pct} onChange={set("test_share_pct")} style={{ padding: 0 }} data-testid="nl-share" />
          <div className="text-[10px] text-eb-muted">{c.test_share_pct === 50 ? "kompletter 50/50-Split" : `Rest ${100 - 2 * c.test_share_pct} % erhält später den Gewinner`}</div>
        </div>
        <div><div className="overline mb-1">Link (CTA)</div><input value={c.cta_url} onChange={set("cta_url")} data-testid="nl-cta" /></div>
        <div><div className="overline mb-1">Button-Text</div><input value={c.cta_label} onChange={set("cta_label")} data-testid="nl-cta-label" /></div>
      </div>
      <input placeholder="Fußzeile / Impressum (optional)" value={c.footer} onChange={set("footer")} data-testid="nl-footer" />
      <button className="btn btn-primary" onClick={save} disabled={busy === "save" || !c.name || !c.subject_a || !c.body_a} data-testid="nl-create">
        {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : editing ? <Check size={14} /> : <Plus size={14} />} {editing ? "Änderungen speichern" : "Entwurf speichern"}
      </button>
    </div>
  );
}

const STATUS = { draft: ["Entwurf", "text-eb-muted"], awaiting_approval: ["wartet auf Freigabe", "text-amber-400"], sent: ["versendet", "text-emerald-400"], winner_sent: ["Gewinner versendet", "text-emerald-400"] };

function VariantBar({ label, v, win }) {
  return (
    <div className={`p-2 rounded-md eb-surface ${win ? "ring-1 ring-eb-primary" : ""}`}>
      <div className="flex justify-between text-xs font-bold">{label}{win && <Trophy size={12} className="text-eb-primary" />}</div>
      <div className="text-[11px] text-eb-muted">{v.sent} gesendet</div>
      {[["Öffnungsrate", v.open_rate], ["Klickrate", v.click_rate]].map(([k, val]) => (
        <div key={k} className="mt-1">
          <div className="flex justify-between text-[10px]"><span>{k}</span><span className="font-mono">{val} %</span></div>
          <div className="h-1.5 bg-white/5 rounded"><div className="h-full rounded bg-eb-primary" style={{ width: `${Math.min(100, val * 2)}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function CampaignList({ campaigns, reload, flash, onEdit }) {
  const [busy, setBusy] = useState(null);
  const [testFor, setTestFor] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const [testTo, setTestTo] = useState(DEFAULT_TEST_TO);
  const sendTest = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/newsletter/campaigns/${id}/test`, { to: testTo.split(/[,;\s]+/).filter(Boolean) });
      flash(`✓ Testversand über ${r.data.transport}: ${r.data.sent} Mails${r.data.errors.length ? ` · Fehler: ${r.data.errors.join("; ")}` : ""}`); setTestFor(null); reload();
    } catch (e) { flash(e.response?.data?.detail || "Testversand fehlgeschlagen"); }
    finally { setBusy(null); }
  };
  const run = async (id, path, okMsg) => {
    setBusy(id);
    try { const r = await axios.post(`${API}/newsletter/campaigns/${id}/${path}`); flash(okMsg(r.data)); await reload(); }
    catch (e) { flash(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(null); }
  };
  const del = async (c) => {
    if (!window.confirm(`Kampagne „${c.name}“ wirklich löschen?${c.status === "sent" ? " Der Versand-Report geht damit verloren." : ""}`)) return;
    await axios.delete(`${API}/newsletter/campaigns/${c.id}`); reload();
  };
  const preview = async (c) => {
    const win = window.open("", "_blank");
    try {
      const r = await axios.get(`${API}/newsletter/campaigns/${c.id}/preview`, { responseType: "text" });
      if (win) {
        const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "text/html;charset=utf-8" }));
        win.location.href = blobUrl;
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
      } else flash("Pop-up blockiert – bitte Pop-ups für diese Seite erlauben.");
    } catch (e) {
      if (win) win.close();
      flash(e.response?.data?.detail || "Vorschau konnte nicht geladen werden");
    }
  };
  return (
    <div className="space-y-3" data-testid="campaign-list">
      {campaigns.map((c) => {
        const s = c.stats; const [label, col] = STATUS[c.status] || [c.status, ""];
        return (
          <div key={c.id} className="card" data-testid={`campaign-${c.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <div className="font-semibold">{c.name}</div>
                <div className="text-[11px] text-eb-muted">{c.ab ? <><FlaskConical size={11} className="inline" /> A/B-Test · {c.test_share_pct} % je Variante</> : "Einzelversand"} · {c.segment_tag || "alle"}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${col}`} data-testid={`campaign-status-${c.id}`}>{label}</span>
                <button className="btn !py-1 text-xs" onClick={() => preview(c)} data-testid={`campaign-preview-${c.id}`}><Eye size={12} /> Vorschau</button>
                {c.status === "draft" && <button className="btn !py-1 text-xs" onClick={() => onEdit(c)} data-testid={`campaign-edit-${c.id}`}><Pencil size={12} /> Bearbeiten</button>}
                <button className="btn !py-1 text-xs" onClick={() => setTestFor(testFor === c.id ? null : c.id)} data-testid={`campaign-test-${c.id}`}><FlaskConical size={12} /> Testversand</button>
                {(c.brevo_runs || []).length > 0 && (
                  <button className="btn !py-1 text-xs" onClick={() => setReportFor(reportFor === c.id ? null : c.id)} data-testid={`campaign-report-${c.id}`}><BarChart3 size={12} /> Versand-Report</button>
                )}
                {c.status === "draft" && (
                  <button className="btn btn-primary !py-1 text-xs" disabled={busy === c.id} data-testid={`campaign-send-${c.id}`}
                    onClick={() => run(c.id, "send", (d) => d.status === "pending" ? "✓ Versand wartet auf Freigabe im Marketing-Hub" : "✓ Automatisch versendet")}>
                    {busy === c.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Versand anfragen
                  </button>
                )}
                {c.status === "sent" && c.ab && (
                  <button className="btn !py-1 text-xs" disabled={busy === c.id} data-testid={`campaign-winner-${c.id}`}
                    onClick={() => run(c.id, "send-winner", (d) => `✓ Gewinner an ${d.sent} weitere Empfänger`)}>
                    <Trophy size={12} /> Gewinner an Rest
                  </button>
                )}
                <button className="text-eb-muted hover:text-rose-400" onClick={() => del(c)} data-testid={`campaign-delete-${c.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
            {testFor === c.id && (
              <div className="mt-2 flex gap-1" data-testid={`campaign-test-form-${c.id}`}>
                <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Test-Adressen, kommagetrennt" data-testid={`campaign-test-to-${c.id}`} />
                <button className="btn btn-primary" onClick={() => sendTest(c.id)} disabled={busy === c.id} data-testid={`campaign-test-send-${c.id}`}>
                  {busy === c.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Senden
                </button>
              </div>
            )}
            {(c.brevo_runs || []).length > 0 && (
              <div className="text-[11px] text-eb-muted mt-1" data-testid={`campaign-brevo-summary-${c.id}`}>
                Über Brevo versendet: {c.brevo_runs.map((r) => `${r.source_name} → ${r.recipients}`).join(" · ")} · gesamt {c.brevo_runs.reduce((a, r) => a + (r.recipients || 0), 0)} Empfänger
              </div>
            )}
            {reportFor === c.id && <CampaignReport campaignId={c.id} />}
            {c.last_test_at && <div className="text-[10px] text-eb-muted mt-1">Letzter Test: {c.last_test_at.slice(0, 16).replace("T", " ")} → {(c.last_test_to || []).join(", ")}</div>}
            {s.total_sent > 0 && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_200px] gap-2">
                <VariantBar label={`A · ${c.subject_a.slice(0, 40)}`} v={s.variants.a} win={s.winner === "a"} />
                {c.ab && <VariantBar label={`B · ${(c.subject_b || c.subject_a).slice(0, 40)}`} v={s.variants.b} win={s.winner === "b"} />}
                {s.test && (
                  <div className="p-2 rounded-md eb-surface text-[11px]" data-testid={`campaign-significance-${c.id}`}>
                    <div className="overline">Signifikanz</div>
                    <div className="font-mono mt-1">Konfidenz {s.test.confidence_pct} %</div>
                    <div className="font-mono text-eb-muted">p = {s.test.p_value}</div>
                    <div className={s.test.significant ? "text-emerald-400" : "text-amber-400"}>{s.test.significant ? "signifikant ✓" : "noch nicht signifikant"}</div>
                    <div className="text-eb-muted mt-1">Gesamt: {s.total_sent}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
